import { useMemo } from 'react';

import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { filterFiltersByUrl } from '@/modules/analytics/constants';
import { selectFilters } from '@/modules/analytics/redux/selectors';
import { ratingsFilterToSet } from '@/modules/analytics/utils/expandRatings';
import { selectAccountId } from '@/redux/session/selectors';
import { getAnalyticsRangeUTC } from '@/util/analytics';
import { parseFilter } from '@/util/util';

/**
 * Builds the GraphQL variables for `ai_agents_overview` and `ai_agents_metrics`.
 *
 * Most filters are passed through `parseFilter` as Postgres set literals
 * (`_uuid` / `_text` / `_int4`), but two need custom handling:
 *   - `firstMessageType` ('proactive' | 'reactive') → `is_proactive` (Boolean)
 *   - `ratings` ([N] + ratingsMode) → `csat_brain` (`_int4`) — expanded to the
 *     explicit list of CSAT scores the threshold + mode imply.
 */
export const useAiAgentsVariables = (dateOverride?: { startDate: string; endDate: string }) => {
  const accountId = useSelector(selectAccountId);
  const filters = useSelector(selectFilters);
  const location = useLocation();

  const effectiveStartDate = dateOverride?.startDate ?? filters.startDate;
  const effectiveEndDate = dateOverride?.endDate ?? filters.endDate;

  const variables = useMemo(() => {
    const allowedFilters = filters.analytics.filter((filter) =>
      filterFiltersByUrl(filter.type, location.pathname)
    );

    const vars: Record<string, unknown> = {
      ...getAnalyticsRangeUTC(effectiveStartDate, effectiveEndDate),
    };

    for (const filter of allowedFilters) {
      if (filter.type === 'firstMessageType') {
        if (filter.firstMessageType === 'proactive') {
          vars.isProactive = true;
        } else if (filter.firstMessageType === 'reactive') {
          vars.isProactive = false;
        }
        continue;
      }

      if (filter.type === 'ratings') {
        vars.csatBrain = ratingsFilterToSet(filter.ratings, filter.ratingsMode);
        continue;
      }

      vars[filter.type] = parseFilter(filter);
    }

    return vars;
  }, [filters.analytics, effectiveEndDate, effectiveStartDate, location.pathname]);

  const skip = !accountId || !filters.filtersLoaded;

  return { variables, skip };
};
